<template>
  <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-12">
    <div class="mb-8 mt-4">
      <h1 class="text-3xl font-bold text-slate-100">Configurações do seu Ambiente</h1>
      <p class="text-slate-400 mt-2 text-sm">Logo, dados da empresa e integração de IA</p>
    </div>

    <div class="rounded-xl border border-cyan-400/30 bg-slate-900/80 backdrop-blur-sm shadow-xl shadow-black/30 p-6">
      <form @submit.prevent="saveTenantSettings" class="space-y-6">
        <div>
          <h2 class="text-lg font-semibold text-slate-100 mb-4">Informações do Ambiente</h2>
          <div class="space-y-4">
            <div>
              <ImageCropUpload
                v-model="form.logo_url"
                upload-strategy="tenant_put"
                variant="logo"
                :company-name="form.name"
                :tenant-name="form.name"
                :tenant-phone="form.phone"
                label-under-photo-color-class="text-slate-200"
                @upload-success="onLogoUploadSuccess"
              />
              <p class="text-center text-xs text-slate-400 mt-1">Essa imagem identifica seu ambiente nas telas públicas.</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Nome do seu Ambiente</label>
              <div class="w-full px-4 py-2.5 rounded-lg border border-slate-600/60 bg-slate-950/40 text-slate-300 font-mono text-sm">
                {{ form.master_code || '—' }}
              </div>
              <p class="text-xs text-slate-500 mt-1">Não pode ser alterado</p>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Nome da sua Empresa *</label>
              <input
                v-model="form.name"
                type="text"
                required
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
              <span v-if="errors.name" class="text-red-400 text-sm">{{ errors.name }}</span>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Telefone principal da empresa</label>
              <input
                v-model="form.phone"
                type="tel"
                placeholder="(00) 00000-0000"
                @input="onPhoneInput"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
              />
              <p class="text-xs text-slate-500 mt-1">Exibido nas telas públicas do ambiente; usado no botão WhatsApp.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Cidade</label>
                <input
                  v-model="form.city"
                  type="text"
                  placeholder="Ex: São Paulo"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">UF</label>
                <select
                  v-model="form.state"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
                >
                  <option value="" class="bg-slate-900">Selecione</option>
                  <option
                    v-for="uf in stateOptions"
                    :key="uf.value"
                    :value="uf.value"
                    class="bg-slate-900"
                  >
                    {{ uf.label }} ({{ uf.value }})
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Timezone do ambiente</label>
              <select
                v-model="form.timezone"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
              >
                <option v-for="tz in timezoneOptions" :key="tz.value" :value="tz.value" class="bg-slate-900">
                  {{ tz.label }}
                </option>
              </select>
              <p class="text-xs text-slate-500 mt-1">
                Usado para interpretar “hoje”, “mês atual” e execuções agendadas no horário esperado.
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Perfil do Instagram</label>
                <input
                  v-model="form.instagram_profile"
                  type="text"
                  placeholder="Ex: https://instagram.com/seu_perfil"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-1">Perfil do Facebook</label>
                <input
                  v-model="form.facebook_profile"
                  type="text"
                  placeholder="Ex: https://facebook.com/sua_pagina"
                  class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="pt-6 border-t border-slate-700/70">
          <h2 class="text-lg font-semibold text-slate-100 mb-2">Integração de IA (agentes)</h2>
          <p class="text-sm text-slate-400 mb-4">
            Uma configuração ativa por ambiente: escolhe o <strong class="text-slate-300">provedor</strong>, o
            <strong class="text-slate-300">modelo</strong> e a
            <strong class="text-slate-300">chave API</strong> (guardada encriptada).
            A execução real usa apenas a chave configurada neste ambiente.
          </p>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1" for="tenant-llm-provider">Provedor</label>
              <select
                id="tenant-llm-provider"
                v-model="form.llm_provider"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 text-sm"
              >
                <option v-for="opt in llmProviderOptions" :key="opt.value" :value="opt.value" class="bg-slate-900">
                  {{ opt.label }}
                </option>
              </select>
              <p v-if="form.llm_provider !== 'openai'" class="text-xs text-amber-300/90 mt-1">
                Este provedor ainda não está integrado — a execução falhará até ser suportado. Use OpenAI por agora.
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1" for="tenant-llm-model">Modelo</label>
              <input
                id="tenant-llm-model"
                v-model="form.llm_model"
                type="text"
                placeholder="gpt-4o-mini"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 font-mono text-sm"
              />
              <p class="text-xs text-slate-500 mt-1">{{ llmModelHint }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1" for="tenant-llm-key">Chave da API (secret)</label>
              <input
                id="tenant-llm-key"
                v-model="form.llm_api_key"
                type="password"
                autocomplete="off"
                :placeholder="llmApiKeyConfigured ? '••••••••  Deixe em branco para manter a chave atual' : 'Cole a chave do provedor escolhido'"
                class="w-full px-3 py-2 rounded-lg border border-slate-500/80 bg-slate-800/55 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/70 font-mono text-sm"
              />
              <p v-if="llmApiKeyConfigured" class="text-xs text-emerald-300/90 mt-1">Chave configurada (oculta).</p>
            </div>
            <div class="flex items-start gap-2">
              <input
                id="tenant-clear-llm-key"
                v-model="form.clear_llm_api_key"
                type="checkbox"
                class="mt-1 rounded border-slate-500 bg-slate-800 text-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:ring-offset-0 focus:ring-offset-slate-900"
              />
              <label for="tenant-clear-llm-key" class="text-sm text-slate-300">
                Remover chave armazenada (execuções reais ficam indisponíveis até configurar nova chave)
              </label>
            </div>
          </div>
        </div>

        <div
          v-if="successMessage"
          class="bg-emerald-500/10 border border-emerald-400/35 text-emerald-200 px-4 py-3 rounded-lg flex items-center space-x-2"
        >
          <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ successMessage }}</span>
        </div>

        <div
          v-if="errors.general"
          class="bg-red-500/10 border border-red-400/40 text-red-300 px-4 py-3 rounded-lg flex items-center space-x-2"
        >
          <svg class="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <span>{{ errors.general }}</span>
        </div>

        <div class="flex justify-end space-x-3 pt-6 border-t border-slate-700/70">
          <button
            type="submit"
            :disabled="saving"
            class="px-6 py-2 rounded-lg bg-[#2AD7DE] hover:bg-[#5de9f0] text-slate-950 font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ saving ? 'Salvando...' : 'Salvar Alterações' }}
          </button>
          <button
            type="button"
            @click="goBack"
            class="px-6 py-2 rounded-lg border border-slate-500/80 text-slate-200 hover:bg-slate-800/60 transition font-medium"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { navigateTo } from '../router.js'
import { useApi } from '../composables/useApi.js'
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import { BRAZILIAN_STATES } from '../constants/brazilianStates.js'
import ImageCropUpload from './ImageCropUpload.vue'

export default {
  components: { ImageCropUpload },
  data() {
    return {
      llmProviderOptions: [
        { value: 'openai', label: 'OpenAI' },
        { value: 'gemini', label: 'Google Gemini (em breve)' },
        { value: 'anthropic', label: 'Anthropic (em breve)' }
      ],
      form: {
        name: '',
        master_code: '',
        phone: '',
        city: '',
        state: '',
        timezone: 'America/Sao_Paulo',
        instagram_profile: '',
        facebook_profile: '',
        logo_url: '',
        llm_provider: 'openai',
        llm_model: 'gpt-4o-mini',
        llm_api_key: '',
        clear_llm_api_key: false
      },
      llmApiKeyConfigured: false,
      stateOptions: BRAZILIAN_STATES,
      timezoneOptions: [
        { value: 'America/Sao_Paulo', label: 'Brasil — Brasília/São Paulo (padrão)' },
        { value: 'America/Fortaleza', label: 'Brasil — Fortaleza' },
        { value: 'America/Recife', label: 'Brasil — Recife' },
        { value: 'America/Maceio', label: 'Brasil — Maceió' },
        { value: 'America/Bahia', label: 'Brasil — Bahia' },
        { value: 'America/Belem', label: 'Brasil — Belém' },
        { value: 'America/Cuiaba', label: 'Brasil — Cuiabá' },
        { value: 'America/Manaus', label: 'Brasil — Manaus' },
        { value: 'America/Porto_Velho', label: 'Brasil — Porto Velho' },
        { value: 'America/Boa_Vista', label: 'Brasil — Boa Vista' },
        { value: 'America/Rio_Branco', label: 'Brasil — Rio Branco' }
      ],
      saving: false,
      successMessage: '',
      errors: {}
    }
  },

  async mounted() {
    this.loadTenantData()
    await this.refreshTenantFromApi()
  },

  computed: {
    llmModelHint() {
      const p = this.form.llm_provider
      if (p === 'openai') {
        return 'Identificador aceite pela API OpenAI (ex.: gpt-4o-mini, gpt-4o).'
      }
      if (p === 'gemini') {
        return 'Quando disponível: identificador do modelo Gemini (ex.: gemini-1.5-flash).'
      }
      if (p === 'anthropic') {
        return 'Quando disponível: identificador Claude na API Anthropic.'
      }
      return 'Identificador do modelo no provedor escolhido.'
    }
  },

  methods: {
    onPhoneInput(event) {
      const { handlePhoneInput } = useBrazilianMasks()
      handlePhoneInput(event, this.form)
    },

    mergeTenantSnapshot(tenant) {
      if (!tenant) return
      this.form.name = tenant.name || ''
      this.form.master_code = tenant.master_code || ''
      this.form.phone = tenant.phone || ''
      this.form.city = tenant.city || ''
      this.form.state = tenant.state || ''
      this.form.timezone = tenant.timezone || 'America/Sao_Paulo'
      this.form.instagram_profile = tenant.instagram_profile || ''
      this.form.facebook_profile = tenant.facebook_profile || ''
      this.form.logo_url = tenant.logo_url || ''
      this.form.llm_provider = tenant.llm_provider || 'openai'
      this.form.llm_model = tenant.llm_model || 'gpt-4o-mini'
      this.llmApiKeyConfigured = !!tenant.llm_api_key_configured
    },

    loadTenantData() {
      const tenantData = localStorage.getItem('tenant')
      if (tenantData) {
        try {
          this.mergeTenantSnapshot(JSON.parse(tenantData))
        } catch {
          /* ignore */
        }
      }
    },

    async refreshTenantFromApi() {
      const { get } = useApi()
      const { data, error } = await get('/api/v1/tenant')
      if (error || !data) return
      this.mergeTenantSnapshot(data)
      const prev = JSON.parse(localStorage.getItem('tenant') || '{}')
      localStorage.setItem(
        'tenant',
        JSON.stringify({
          ...prev,
          ...data
        })
      )
    },

    onLogoUploadSuccess({ tenant }) {
      if (tenant) {
        const updatedTenant = {
          ...JSON.parse(localStorage.getItem('tenant') || '{}'),
          name: tenant.name,
          master_code: tenant.master_code,
          phone: tenant.phone || '',
          city: tenant.city || '',
          state: tenant.state || '',
          instagram_profile: tenant.instagram_profile || '',
          facebook_profile: tenant.facebook_profile || '',
          logo_url: tenant.logo_url || '',
          llm_provider: tenant.llm_provider,
          llm_model: tenant.llm_model,
          llm_api_key_configured: tenant.llm_api_key_configured
        }
        localStorage.setItem('tenant', JSON.stringify(updatedTenant))
        this.form.logo_url = tenant.logo_url || ''
      }
    },

    async saveTenantSettings() {
      this.saving = true
      this.errors = {}
      this.successMessage = ''

      try {
        const { put } = useApi()
        const tenantPayload = {
          name: this.form.name,
          phone: this.form.phone || '',
          city: this.form.city || '',
          state: this.form.state || '',
          timezone: this.form.timezone || 'America/Sao_Paulo',
          instagram_profile: this.form.instagram_profile || '',
          facebook_profile: this.form.facebook_profile || '',
          llm_provider: this.form.llm_provider || 'openai',
          llm_model: this.form.llm_model || ''
        }
        if (this.form.llm_api_key && String(this.form.llm_api_key).trim()) {
          tenantPayload.llm_api_key = String(this.form.llm_api_key).trim()
        }
        if (this.form.clear_llm_api_key) {
          tenantPayload.clear_llm_api_key = true
        }

        const { data, error } = await put('/api/v1/tenant', { tenant: tenantPayload })

        if (error) {
          const errorList = error.response?.data?.errors
          if (Array.isArray(errorList)) {
            this.errors.general = errorList.join(', ')
          } else if (typeof errorList === 'object') {
            const firstKey = Object.keys(errorList)[0]
            this.errors.general = errorList[firstKey]?.[0] || 'Erro ao salvar configurações'
          } else {
            this.errors.general = errorList || 'Erro ao salvar configurações'
          }
          return
        }

        const updatedTenant = {
          ...JSON.parse(localStorage.getItem('tenant') || '{}'),
          name: data.name,
          master_code: data.master_code,
          phone: data.phone || '',
          city: data.city || '',
          state: data.state || '',
          timezone: data.timezone || 'America/Sao_Paulo',
          instagram_profile: data.instagram_profile || '',
          facebook_profile: data.facebook_profile || '',
          logo_url: data.logo_url || '',
          llm_provider: data.llm_provider,
          llm_model: data.llm_model,
          llm_api_key_configured: data.llm_api_key_configured
        }
        localStorage.setItem('tenant', JSON.stringify(updatedTenant))
        this.form.logo_url = data.logo_url || ''
        this.form.llm_provider = data.llm_provider || 'openai'
        this.form.llm_model = data.llm_model || 'gpt-4o-mini'
        this.form.llm_api_key = ''
        this.form.clear_llm_api_key = false
        this.llmApiKeyConfigured = !!data.llm_api_key_configured

        this.successMessage = 'Configurações atualizadas com sucesso!'
        setTimeout(() => { this.successMessage = '' }, 3000)
      } catch (err) {
        this.errors.general = 'Erro ao salvar configurações'
      } finally {
        this.saving = false
      }
    },

    goBack() {
      navigateTo('agent-dashboard')
    }
  }
}
</script>
